<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";

const prompts = [
  "Write an application to the principal for sick leave.",
  "Write an application for fee concession.",
  "Write an application for remission of fine.",
  "Write an application for character certificate.",
  "Write an application for leave to attend a marriage ceremony.",
  "Write an application for duplicate library card.",
];

const formatSteps = [
  "To + designation",
  "Institution name",
  "Subject line",
  "Sir,",
  "Short request body",
  "Polite closing",
  "Yours obediently + name",
];

const requestTypes = [
  "Leave request",
  "Fee concession request",
  "Certificate request",
  "Fine remission request",
  "Document request",
  "Permission request",
];

const bodyFramework = [
  "Reason ko first sentence me clear karo.",
  "Duration ya exact request ko second sentence me mention karo.",
  "Closing line me polite request repeat karo.",
];

const quickSubjectLines = [
  "Subject: Application for Sick Leave",
  "Subject: Application for Fee Concession",
  "Subject: Application for Character Certificate",
  "Subject: Application for Remission of Fine",
];

const applicationMistakes = [
  "Subject line miss kar dena common mistake hai.",
  "Application ko letter ki tarah emotional bana dena avoid karo.",
  "Request ko vague rakhne ke bajaye clear aur specific rakho.",
  "College/school head ko address karte waqt wrong designation use na karo.",
];

const sampleApplication = [
  "To",
  "The Principal,",
  "ABC College, Lahore.",
  "Subject: Application for Sick Leave",
  "Sir,",
  "Respectfully, I beg to say that I am suffering from fever and the doctor has advised me to take rest for two days. Therefore, I request you to grant me leave for two days.",
  "Yours obediently,",
  "Ali",
];

usePageSeo({
  title: "11th Class English Applications | Application Writing Format and Samples",
  description: "11th class English applications page with format, common prompts, subject-line examples and mini sample application.",
  keywords: "11th class english applications, class 11 application writing, english application format, leave application, application to principal",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th English", item: "/11th/english/" },
    { name: "Grammar", item: "/11th/english/grammar/" },
    { name: "Applications", item: "/11th/english/grammar/applications/" },
  ],
});
</script>

<template>
  <PageContainer>
    <EnglishHero
      headline="Grammar Task"
      title="Applications"
      description="Yeh page principal aur institutional applications ke liye direct board-style support deta hai."
    />

    <EnglishSection title="Standard Format" description="Keep this structure in mind before writing any application.">
      <EnglishGrid>
        <EnglishCard v-for="item in formatSteps" :key="item" title="Format Step" :description="item" />
      </EnglishGrid>
    </EnglishSection>

    <EnglishSection title="Common Application Prompts" description="These are the prompt types that repeat most often in board prep.">
      <EnglishGrid>
        <EnglishCard v-for="item in prompts" :key="item" title="Prompt" :description="item" />
      </EnglishGrid>
    </EnglishSection>

    <EnglishSection title="Common Request Types" description="Use these to quickly identify the kind of application being asked.">
      <EnglishGrid>
        <EnglishCard v-for="item in requestTypes" :key="item" title="Request Type" :description="item" />
      </EnglishGrid>
    </EnglishSection>

    <EnglishSection title="Body Framework" description="This sequence keeps the application short and exam-safe.">
      <EnglishGrid>
        <EnglishCard v-for="item in bodyFramework" :key="item" title="Body Rule" :description="item" />
      </EnglishGrid>
    </EnglishSection>

    <EnglishSection title="Quick Subject Lines" description="Use these as ready-made subject patterns.">
      <EnglishGrid>
        <EnglishCard v-for="item in quickSubjectLines" :key="item" title="Subject Line" :description="item" />
      </EnglishGrid>
    </EnglishSection>

    <EnglishSection title="Mini Sample Application" description="A compact model you can adapt in exam conditions.">
      <EnglishLinkList :links="sampleApplication.map((line) => ({ label: line }))" />
    </EnglishSection>

    <EnglishSection title="Common Mistakes" description="Avoid these errors to keep the response formal and clear.">
      <EnglishGrid>
        <EnglishCard v-for="item in applicationMistakes" :key="item" title="Mistake" :description="item" />
      </EnglishGrid>
    </EnglishSection>
  </PageContainer>
</template>
